import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormControlStatus,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subject, take, takeUntil } from 'rxjs';
import { InvalidFieldComponent } from 'src/app/shared/components';
import { SanitizerPipe } from 'src/app/shared/pipes';
import { CurrentFacilityService, SummaryService } from 'src/app/shared/services';
import { TermsOfUseForm } from 'src/app/shared/services/summary/models/types';
import { TermsOfUSeFormErrorJSON, TermsOfUseFormGroupType } from './models/terms-of-use-form';
import { formTOUErrors } from './utils/form-errors';

@Component({
    selector: 'app-terms-of-use',
    templateUrl: './terms-of-use.component.html',
    styleUrls: ['./terms-of-use.component.scss'],
    standalone: true,
    imports: [
        MatDividerModule,
        MatCheckboxModule,
        MatFormFieldModule,
        InvalidFieldComponent,
        ReactiveFormsModule,
        SanitizerPipe,
    ],
})
export class TermsOfUseComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    public termsOfUseForm: TermsOfUseFormGroupType;
    public formError: TermsOfUSeFormErrorJSON;
    public termsOfUseURL: string;

    @Input() showError = false;

    get termsOfUse(): AbstractControl | null {
        return this.termsOfUseForm.get('termsOfUse');
    }

    constructor(
        private fb: NonNullableFormBuilder,
        private summaryService: SummaryService,
        private currentFacilityService: CurrentFacilityService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.getTermsOfUseUrl();
        this.createForm();
        this.subscribeFormChanges();
        this.subscribeTermsOfUse();
        this.setFormError();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createForm(): void {
        this.termsOfUseForm = this.fb.group({
            termsOfUse: ['', Validators.required],
        });
    }

    setFormError(): void {
        this.formError = formTOUErrors;
    }

    subscribeFormChanges(): void {
        this.termsOfUseForm.statusChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((statusChanges: FormControlStatus) => {
                this.summaryService.setTermsOfUseValid({
                    isValidForm: statusChanges === 'VALID',
                    termsOfUseFormGroup: this.termsOfUseForm,
                });
            });
    }

    subscribeTermsOfUse(): void {
        this.summaryService
            .getTermsOfUseValid$()
            .pipe(take(1))
            .subscribe((termsOfUse: TermsOfUseForm) => {
                if (termsOfUse?.isValidForm) {
                    this.termsOfUse?.patchValue({ termsOfUse: true });
                } else {
                    this.summaryService.setTermsOfUseValid({
                        isValidForm: false,
                        termsOfUseFormGroup: this.termsOfUseForm,
                    });
                }
            });
    }

    getTermsOfUseUrl(): void {
        this.termsOfUseURL = this.currentFacilityService.getTermsOfUseURL();
    }
}
